import React, { useEffect, useRef, useState } from 'react';

import { HELPERS } from '@/utils';
import { Icon } from '@iconify/react';
import ReactPixel from 'react-facebook-pixel';
import { sendCustomTag } from '@/utils/clarity-tags';
import { setBannerVisibility } from '@/hooks/useBannerVisibility';
import { useBanner } from '@/hooks/useBanner';

const customStyles = `
  .banner-component {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 60;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .banner-shimmer-wrap {
    position: absolute;
    inset: 0;
    pointer-events: none;
    animation: shimmerMove 4s ease-in-out infinite;
  }

  @keyframes shimmerMove {
    0% {
      transform: translateX(-100%) translateZ(0);
      opacity: 0;
    }
    20% {
      opacity: 1;
    }
    80% {
      opacity: 1;
    }
    100% {
      transform: translateX(200%) translateZ(0);
      opacity: 0;
    }
  }

  .banner-shimmer {
    width: 100px;
    height: 200%;
    background: linear-gradient(90deg, 
      transparent 0%,
      rgba(255, 255, 255, 0.2) 20%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0.2) 80%,
      transparent 100%
    );
    filter: blur(40px);
    transform: rotate(25deg) translateY(-50%);
    position: absolute;
    top: 50%;
  }

  .banner-slide-in {
    animation: slideDown 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .banner-slide-out {
    animation: slideUp 0.3s ease-in forwards;
  }

  @keyframes slideUp {
    to {
      transform: translateY(-100%);
      opacity: 0;
    }
  }

  .banner-content-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 56px;
    padding: 12px 0;
  }

  .banner-close-button {
    transition: all 0.2s ease;
    backdrop-filter: blur(8px);
  }

  .banner-close-button:hover {
    transform: scale(1.1);
    background-color: rgba(255, 255, 255, 0.25);
  }

  .banner-cta-button {
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .banner-cta-button:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    .banner-content-wrapper {
      padding: 8px 0;
      min-height: auto;
    }
    
    .banner-text {
      font-size: 13px !important;
      padding-right: 10px;
    }
    
    .banner-cta-button {
      font-size: 12px !important;
    }
  }
`;

const ShimmerBanner = ({
  theme = 'purple',
  message = 'Dapatkan rekomendasi program belajar bisnis yang tepat untuk Anda!',
  buttonText = 'Ikuti Quiz Gratis',
  link = '#',
  icon = 'sparkles',
  dismissible = true,
  autoHide = false,
  autoHideDelay = 10000
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const bannerRef = useRef(null);

  const themes = {
    purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    blue: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    teal: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
    green: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    orange: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
    pink: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
    dark: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)',
  };

  const icons = {
    rocket: <Icon icon="streamline-emojis:rocket" className="w-5 h-5" />,
    gift: <Icon icon="tabler:gift" className="w-5 h-5" />,
    zap: <Icon icon="tabler:bolt" className="w-5 h-5" />
  };

  const handleClose = () => {
    HELPERS.sendEventGA('banner_dismissed', {
      banner_type: 'sticky_banner',
      banner_theme: theme,
      banner_message: message,
      page_path: window.location.pathname
    });

    ReactPixel.track('CustomEvent', {
      event_name: 'BannerDismissed',
      banner_type: 'shimmer_banner'
    });

    sendCustomTag('banner', 'dismissed');

    setIsAnimatingOut(true);
    setBannerVisibility(false);
    setTimeout(() => {
      setIsVisible(false);
      if (dismissible) {
        sessionStorage.setItem('banner-dismissed', 'true');
      }
    }, 300);
  };

  const handleBannerClick = () => {
    HELPERS.sendEventGA('banner_clicked', {
      banner_type: 'sticky_banner',
      banner_theme: theme,
      banner_message: message,
      button_text: buttonText,
      destination_link: link,
      page_path: window.location.pathname
    });

    ReactPixel.track('CustomEvent', {
      event_name: 'BannerClicked',
      banner_type: 'sticky_banner',
      destination: link
    });

    sendCustomTag('banner', 'clicked');
  };

  useEffect(() => {
    if (dismissible) {
      const wasDismissed = sessionStorage.getItem('banner-dismissed');
      if (wasDismissed === 'true') {
        setIsVisible(false);
      }
    }

    if (isVisible && !isAnimatingOut) {
      setTimeout(() => {
        const height = bannerRef.current?.offsetHeight || 56;
        setBannerVisibility(true, height);
      }, 100);
    }

    if (isVisible && !hasTrackedView) {
      HELPERS.sendEventGA('banner_viewed', {
        banner_type: 'sticky_banner',
        banner_theme: theme,
        banner_message: message,
        page_path: window.location.pathname,
        auto_hide: autoHide,
        auto_hide_delay: autoHideDelay
      });

      ReactPixel.track('ViewContent', {
        content_name: 'Sticky Banner',
        content_category: 'banner',
        banner_theme: theme
      });

      sendCustomTag('banner', 'viewed');
      setHasTrackedView(true);
    }

    if (autoHide && isVisible) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoHideDelay);
      return () => clearTimeout(timer);
    }

    return () => {
      if (isVisible && !isAnimatingOut) {
        setBannerVisibility(false);
      }
    };
  }, [autoHide, autoHideDelay, dismissible, isVisible, hasTrackedView, theme, message, isAnimatingOut]);

  if (!isVisible) return null;

  return (
    <>
      <style>{customStyles}</style>
      <section
        ref={bannerRef}
        className={`banner-component ${isAnimatingOut ? 'banner-slide-out' : 'banner-slide-in'}`}
        style={{ background: themes[theme] || themes.purple }}
      >
        <div className="relative w-full">
          <div className="banner-content-wrapper relative">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-center">

                <div className="flex items-center justify-center gap-2 md:gap-4 flex-1">

                  <div className="hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-white flex-shrink-0">
                    {icons[icon] || icons.rocket}
                  </div>

                  <div className="flex items-center gap-2 text-center">
                    {title && (
                      <span className="hidden md:inline font-semibold text-xs md:text-base lg:text-lg whitespace-nowrap text-white">
                        {title}
                      </span>
                    )}
                    {title && content && (
                      <span className="hidden md:inline text-white">•</span>
                    )}
                    {content && (
                      <span className="font-normal text-xs md:text-sm lg:text-base text-white">
                        {content}
                      </span>
                    )}
                  </div>

                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleBannerClick}
                    className="banner-cta-button inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-2.5 bg-white text-purple-700 font-semibold text-sm md:text-base rounded-full hover:bg-gray-50 transition-all ml-2 md:ml-4 flex-shrink-0"
                  >
                    <span>{buttonText}</span>
                    <Icon icon="tabler:arrow-right" className="w-3 h-3 md:w-4 md:h-4" />
                  </a>
                </div>

                {dismissible && (
                  <button
                    onClick={handleClose}
                    className="banner-close-button absolute right-1 md:right-4 top-1/2 -translate-y-1/2 p-1 md:p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all"
                    aria-label="Close banner"
                  >
                    <Icon icon="tabler:x" className="w-3 h-3 md:w-5 md:h-5 text-white" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="banner-shimmer-wrap">
            <div className="banner-shimmer" />
          </div>
        </div>
      </section>
    </>
  );
};

const CompactShimmerBanner = ({
  message = 'Quiz gratis untuk bisnismu!',
  buttonText = 'Mulai',
  link = '#'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [hasTrackedView, setHasTrackedView] = useState(false);

  useEffect(() => {
    if (isVisible && !hasTrackedView) {
      HELPERS.sendEventGA('banner_viewed', {
        banner_type: 'sticky_banner_compact',
        banner_message: message,
        page_path: window.location.pathname
      });

      ReactPixel.track('ViewContent', {
        content_name: 'Compact Shimmer Banner',
        content_category: 'compact_banner'
      });

      sendCustomTag('banner', 'viewed');
      setHasTrackedView(true);
    }
  }, [isVisible, hasTrackedView, message]);

  const handleBannerClick = () => {
    HELPERS.sendEventGA('banner_clicked', {
      banner_type: 'sticky_banner_compact',
      banner_message: message,
      button_text: buttonText,
      destination_link: link,
      page_path: window.location.pathname
    });

    ReactPixel.track('CustomEvent', {
      event_name: 'BannerClicked',
      banner_type: 'sticky_banner_compact',
      destination: link
    });

    sendCustomTag('banner', 'clicked');
  };

  const handleClose = () => {
    HELPERS.sendEventGA('banner_dismissed', {
      banner_type: 'sticky_banner_compact',
      banner_message: message,
      page_path: window.location.pathname
    });

    ReactPixel.track('CustomEvent', {
      event_name: 'BannerDismissed',
      banner_type: 'sticky_banner_compact'
    });

    sendCustomTag('banner', 'dismissed');
    setBannerVisibility(false);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      <style>{customStyles}</style>
      <section className="banner-component banner-slide-in">
        <div className="relative">
          <div className="flex items-center justify-center py-2 px-3">
            <a
              href={link}
              onClick={handleBannerClick}
              className="flex items-center justify-center gap-3 text-white hover:opacity-90 transition-opacity"
            >
              <span className="text-sm font-medium">{message}</span>
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/20 backdrop-blur text-white font-semibold text-xs rounded-full">
                {buttonText}
                <Icon icon="tabler:arrow-right" className="w-3 h-3" />
              </span>
            </a>

            <button
              onClick={handleClose}
              className="absolute right-2 p-1 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Close"
            >
              <Icon icon="tabler:x" className="w-4 h-4 text-white" />
            </button>
          </div>

          <div className="banner-shimmer-wrap">
            <div className="banner-shimmer" />
          </div>
        </div>
      </section>
    </>
  );
};

const APIShimmerBanner = ({ bannerData, fallbackProps = {}, onDismiss }) => {
  const { data: hookBannerData, isLoading, isError } = useBanner();
  const finalBannerData = bannerData || hookBannerData;
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const bannerRef = useRef(null);
  
  const defaultValues = {
    theme: 'purple',
    message: 'Dapatkan rekomendasi program belajar bisnis yang tepat untuk Anda!',
    buttonText: 'Ikuti Quiz Gratis',
    link: '/personal-learning-program/quiz',
    icon: 'sparkles',
    dismissible: true,
    autoHide: false,
    autoHideDelay: 10000,
    ...fallbackProps
  };
  
  const bannersArray = Array.isArray(finalBannerData) ? finalBannerData : [finalBannerData];
  const banner = bannersArray?.[0] || {};

  const theme = banner.theme || defaultValues.theme;
  const title = banner.title || banner.content || defaultValues.message;
  const content = banner.content || '';
  const buttonText = banner.button_text || defaultValues.buttonText;
  const link = banner.button_link || defaultValues.link;
  const icon = banner.icon || defaultValues.icon;
  const dismissible = banner.show_close_button !== undefined ? banner.show_close_button : defaultValues.dismissible;
  const autoHide = banner.auto_hide !== undefined ? banner.auto_hide : defaultValues.autoHide;
  const autoHideDelay = banner.auto_hide_delay || defaultValues.autoHideDelay;
  const isActive = banner.is_active !== undefined ? banner.is_active : true;

  const themes = {
    purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    blue: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    teal: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
    green: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    orange: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
    pink: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
    dark: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)',
  };

  const icons = {
    sparkles: <Icon icon="tabler:sparkles" className="w-5 h-5" />,
    rocket: <Icon icon="streamline-emojis:rocket" className="w-5 h-5" />,
    gift: <Icon icon="tabler:gift" className="w-5 h-5" />,
    zap: <Icon icon="tabler:bolt" className="w-5 h-5" />,
    info: <Icon icon="tabler:info-circle" className="w-5 h-5" />,
    alert: <Icon icon="tabler:alert-triangle" className="w-5 h-5" />,
    star: <Icon icon="tabler:star" className="w-5 h-5" />
  };

  const handleClose = () => {
    HELPERS.sendEventGA('banner_dismissed', {
      banner_type: 'sticky_banner',
      banner_theme: theme,
      banner_title: title,
      banner_content: content,
      banner_id: banner.id || 'default',
      page_path: window.location.pathname
    });

    ReactPixel.track('CustomEvent', {
      event_name: 'BannerDismissed',
      banner_type: 'sticky_banner',
      banner_id: banner.id || 'default'
    });

    sendCustomTag('banner', 'dismissed');

    setIsAnimatingOut(true);
    setBannerVisibility(false);
    setTimeout(() => {
      setIsVisible(false);
      if (dismissible) {
        sessionStorage.setItem('api-banner-dismissed', 'true');
      }
      if (onDismiss) {
        onDismiss();
      }
    }, 300);
  };

  const handleBannerClick = () => {
    HELPERS.sendEventGA('banner_clicked', {
      banner_type: 'sticky_banner',
      banner_theme: theme,
      banner_title: title,
      banner_content: content,
      button_text: buttonText,
      destination_link: link,
      banner_id: banner.id || 'default',
      page_path: window.location.pathname
    });

    ReactPixel.track('CustomEvent', {
      event_name: 'BannerClicked',
      banner_type: 'sticky_banner',
      destination: link,
      banner_id: banner.id || 'default'
    });

    sendCustomTag('banner', 'clicked');
  };

  useEffect(() => {
    if (dismissible) {
      const wasDismissed = sessionStorage.getItem('api-banner-dismissed');
      if (wasDismissed === 'true') {
        setIsVisible(false);
      }
    }

    if (isVisible && !isAnimatingOut && !isLoading && !isError && isActive) {
      const checkHeight = () => {
        if (bannerRef.current) {
          const height = bannerRef.current.offsetHeight;
          setBannerVisibility(true, height);
        }
      };
      setTimeout(checkHeight, 100);
    } else if (!isVisible || isAnimatingOut) {
      setBannerVisibility(false);
    }

    if (isVisible && !hasTrackedView && isActive && !isLoading && !isError) {
      HELPERS.sendEventGA('banner_viewed', {
        banner_type: 'sticky_banner',
        banner_theme: theme,
        banner_title: title,
        banner_content: content,
        banner_id: banner.id || 'default',
        banner_source: 'api',
        page_path: window.location.pathname,
        auto_hide: autoHide,
        auto_hide_delay: autoHideDelay
      });

      ReactPixel.track('ViewContent', {
        content_name: 'Sticky Banner',
        content_category: 'banner',
        banner_theme: theme,
        banner_id: banner.id || 'default'
      });

      sendCustomTag('banner', 'viewed');
      setHasTrackedView(true);
    }

    if (autoHide && isVisible) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoHideDelay);
      return () => clearTimeout(timer);
    }

    return () => {
      if (isVisible && !isAnimatingOut) {
        setBannerVisibility(false);
      }
    };
  }, [autoHide, autoHideDelay, dismissible, isVisible, hasTrackedView, theme, title, content, banner.id, isActive, isLoading, isError, isAnimatingOut]);

  if (!isVisible || isLoading || isError || !isActive || (!title && !content)) {
    return null;
  }

  return (
    <>
      <style>{customStyles}</style>
      <section
        ref={bannerRef}
        className={`banner-component ${isAnimatingOut ? 'banner-slide-out' : 'banner-slide-in'}`}
        style={{
          background: themes[theme] || themes.purple
        }}
      >
        <div className="relative w-full">
          <div className="banner-content-wrapper relative">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-center">

                <div className="flex items-center justify-center gap-2 md:gap-4 flex-1">

                  <div className="hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-white/20 flex-shrink-0">
                    {icons[icon] || icons.gift}
                  </div>

                  <div className="flex items-center gap-2 text-center">
                    {title && (
                      <span className="hidden md:inline font-semibold text-xs md:text-base lg:text-lg whitespace-nowrap text-white">
                        {title}
                      </span>
                    )}
                    {title && content && (
                      <span className="hidden md:inline text-white">•</span>
                    )}
                    {content && (
                      <span className="font-normal text-xs md:text-sm lg:text-base text-white">
                        {content}
                      </span>
                    )}
                  </div>

                  {buttonText && (
                    <a
                      href={link}
                      target={banner.target || '_self'}
                      rel={banner.target === '_blank' ? 'noopener noreferrer' : undefined}
                      onClick={handleBannerClick}
                      className="banner-cta-button inline-flex items-center gap-1 md:gap-2 px-3 md:px-6 py-1.5 md:py-2.5 bg-white text-purple-700 font-semibold text-xs md:text-base rounded-full hover:bg-gray-50 transition-all ml-2 md:ml-4 mr-8 md:mr-0 flex-shrink-0"
                    >
                      <span>{buttonText}</span>
                      <Icon icon="tabler:arrow-right" className="w-3 h-3 md:w-4 md:h-4" />
                    </a>
                  )}
                </div>

                {dismissible && (
                  <button
                    onClick={handleClose}
                    className="banner-close-button absolute right-2 md:right-4 top-1/2 -translate-y-1/2 p-1.5 md:p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all"
                    aria-label="Close banner"
                  >
                    <Icon icon="tabler:x" className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="banner-shimmer-wrap">
            <div className="banner-shimmer" />
          </div>
        </div>
      </section>
    </>
  );
};

export { CompactShimmerBanner, APIShimmerBanner };
export default ShimmerBanner;